import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import type { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import DownArrowIcon from '../../icons/downArrow.inline.svg'
import SizeGuideModal from '../modal'
import NavigationArrows from '../navigationArrows/NavigationArrows'
import MeasuringGuide from '../sizeChart/components/measuringGuide/MeasuringGuide'
import { SizeGuideProps } from '../sizeGuide/SizeGuide'
import { getDefaultSizeChartUnitIndex, normalizeSizeChartDefaultUnitOrder } from '../sizeGuide/sizeGuide.utils'
import Tabs, { Tab } from '../tabs'

import SizeChartHeaderV2 from './components/SizeChartHeaderV2'
import SizeChartV2, { SizeChartV2Handle, SizeChartV2ScrollState } from './components/SizeChartV2'
import sizeChartStyles from './components/sizeChartV2.module.scss'
import styles from './sizeGuideV2.module.scss'
import { getSizeGuideTabs, TAB_CONFIG } from './utils/utils'

export const SizeGuideV2 = (props: SizeGuideProps) => {
  const localeConfig = useStatsigDynamicLocaleConfig()
  const showLocalisedReviewUI = Boolean(localeConfig.displayLocalisedReviewSize)
  // Reorder size-chart sections so Contentful's `isDefault` unit (e.g. UK → cm) renders FIRST,
  // matching V1 (`normalizeSizeChartDefaultUnitOrder`). Without this the default unit was selected
  // (correct index) but stayed in its original position — e.g. cm highlighted yet shown second.
  // No-op for locales that don't opt in (enabled=false), so US/CA ordering is unchanged.
  const normalizedSizeChart = (normalizeSizeChartDefaultUnitOrder(props.sizeChart, showLocalisedReviewUI) ??
    props.sizeChart) as ContentfulSizeChart
  const [activeTab, setActiveTab] = useState(0)
  // Honour Contentful's `isDefault` flag on size-chart sections for locales that
  // opt into localised size-guide defaults (e.g. UK → cm). Shared with V1 via
  // `getDefaultSizeChartUnitIndex` so any future size-chart variant gets it for free.
  const [activeUnitTab, setActiveUnitTab] = useState(() =>
    getDefaultSizeChartUnitIndex(normalizedSizeChart?.sections, showLocalisedReviewUI),
  )
  const [scrollState, setScrollState] = useState<SizeChartV2ScrollState>({
    showScrollButtons: false,
    canScrollLeft: false,
    canScrollRight: false,
  })
  const sizeChartRef = useRef<SizeChartV2Handle>(null)
  const { t } = useTranslation('common')

  const internationalSizeConversionGuides =
    props.internationalSizeConversionGuides && props.internationalSizeConversionGuides.length > 0
      ? props.internationalSizeConversionGuides[0]
      : undefined
  const sizeChartTabs = getSizeGuideTabs({
    sizeChart: normalizedSizeChart,
    measuringGuides: props.measuringGuides,
    internationalSizeConversionGuides,
    t: t,
  })
  const activeSizeGuideTab = sizeChartTabs[activeTab]
  const productProperties = props.product ? getProductPropertiesForTracking(props.product) : undefined

  const handleUnitsToggle = (index: number, selectedValue: string) => {
    setActiveUnitTab(index)
    trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE, {
      section_type: 'size_chart_toggle',
      context: {
        size_chart_name: normalizedSizeChart.title,
        size_chart_units: selectedValue,
        productProperties: productProperties,
      },
    })
  }

  const handleScrollStateChange = useCallback((state: SizeChartV2ScrollState) => {
    setScrollState(state)
  }, [])

  const prevNavClickHandler = () => {
    sizeChartRef.current?.scrollLeft()
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'click_carousel_left',
      value: true,
      context: {
        // eslint-disable-next-line i18next/no-literal-string
        contentContainerDisplayName: 'SIZE CHART',
        productProperties: productProperties,
      },
    })
  }

  const nextNavClickHandler = () => {
    sizeChartRef.current?.scrollRight()
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'click_carousel_right',
      value: true,
      context: {
        // eslint-disable-next-line i18next/no-literal-string
        contentContainerDisplayName: 'SIZE CHART',
        productProperties: productProperties,
      },
    })
  }

  const showUnitsToggle =
    normalizedSizeChart?.sections?.length > 1 && activeSizeGuideTab?.tabId === TAB_CONFIG.sizeChart.tabId

  return (
    <SizeGuideModal
      open={true}
      onClose={props.onSizeChartModalClose}
      showCloseButton={true}
      responsiveBottomSheet={true}
      className={styles.sizeChartModal}
      closeButtonContainerClassName={styles.stickyCloseButtonContainer}
    >
      <div className={styles.modalContent}>
        <SizeChartHeaderV2
          headerTitle={normalizedSizeChart?.title}
          onUnitsToggle={handleUnitsToggle}
          selectedSizeChartSection={normalizedSizeChart?.sections?.[activeUnitTab]}
          sizeChartSections={normalizedSizeChart?.sections ?? []}
          activeUnitIndex={activeUnitTab}
          showUnitsToggle={showUnitsToggle}
        />
        <div className={styles.tabsRow}>
          <Tabs
            activeTabId={activeSizeGuideTab?.tabId}
            onChange={activeId => {
              const nextTabIndex = sizeChartTabs.findIndex(tab => tab.tabId === activeId)
              if (nextTabIndex >= 0) {
                setActiveTab(nextTabIndex)
                trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB, {
                  section_type: 'size_chart_tab',
                  context: {
                    size_chart_name: normalizedSizeChart?.title,
                    size_chart_tab_value: sizeChartTabs[nextTabIndex]?.tabTitle,
                    productProperties: productProperties,
                  },
                })
              }
            }}
            tabButtonWrapperClassname={styles.tabsContainer__tabButtonWrapper}
            activeTabHeaderClassName={styles.tabsContainer__tabHeader__active}
          >
            {sizeChartTabs.map(tab => (
              <Tab
                key={tab.tabId}
                id={tab.tabId}
                title={tab.tabTitle}
                headerClassName={styles.tabsContainer__tabHeader}
              >
                {tab?.sizeChart && (
                  <>
                    <SizeChartV2
                      ref={sizeChartRef}
                      activeSizeGuideUnitIndex={activeUnitTab}
                      sizeChart={tab.sizeChart}
                      subtitle={tab.sizeChart.subtitle}
                      description={tab.sizeChart.description}
                      onScrollStateChange={handleScrollStateChange}
                    />
                    {tab.sizeChart.additionalSizeChartGuide?.map((guide, index) => (
                      <div key={`additional-guide-${index}`} className={sizeChartStyles.additionalSizeChart}>
                        <SizeChartV2
                          activeSizeGuideUnitIndex={activeUnitTab}
                          sizeChart={guide}
                          subtitle={guide.subtitle}
                          description={guide.description}
                        />
                      </div>
                    ))}
                  </>
                )}
                {tab?.measuringGuides?.length > 0 && (
                  <div className={styles.measuringGuide}>
                    {tab?.measuringGuides?.length <= 1 && (
                      <div className={styles.measuringGuide_title}>{t('measuring_guide')}</div>
                    )}
                    {tab?.measuringGuides?.map((guide, i) => (
                      <MeasuringGuide
                        key={i}
                        measuringGuide={guide}
                        showTitle={tab?.measuringGuides?.length > 1}
                        measuringGuideWrapperClassName={
                          tab?.measuringGuides?.length > 1 ? styles.measuringGuideWrapperMultiple : ''
                        }
                        imageWrapperClassName={styles.measuringGuideImageWrapper}
                      />
                    ))}
                  </div>
                )}
                {tab?.internationalSizeChart && (
                  <SizeChartV2
                    ref={sizeChartRef}
                    activeSizeGuideUnitIndex={0}
                    sizeChart={tab.internationalSizeChart}
                    onScrollStateChange={handleScrollStateChange}
                  />
                )}
              </Tab>
            ))}
          </Tabs>
          {scrollState.showScrollButtons && (
            <NavigationArrows
              onPrevClick={prevNavClickHandler}
              onNextClick={nextNavClickHandler}
              prevButtonDisabled={!scrollState.canScrollLeft}
              nextButtonDisabled={!scrollState.canScrollRight}
              hideArrowOnMobile={true}
              className={styles.scrollArrows}
              icon={DownArrowIcon}
              prevClassName={styles.prevIcon}
              nextClassName={styles.nextIcon}
            />
          )}
        </div>
      </div>
    </SizeGuideModal>
  )
}
